import { ReactNode } from 'react'
import { Box, useTheme } from '@mui/material'

import { TextBold, TextParagraph, TextSubTitle } from 'presentation/components/elements'
import { StyledRating } from 'presentation/styles/index.styled'
import {
  daysText,
  nightsText,
  showMoreText,
  sinceText,
  stayText,
} from 'presentation/constant/globals'
import {
  forMoreInformationText,
  votesText,
} from 'presentation/constant/texts/availableDestinationCard'
import ImageIcon from 'presentation/assets/images/picture.png'
import { convertStringPrice } from 'presentation/utils/format'

type AvailableDestinationCardProps = {
  children: ReactNode
  imageUrl: string
  name: string
  destinationName: string
  price: number
  rating: number
  description: string
  numberDays: number
  numberNights: number
  votes?: number
  isShowNumberOfVotes?: boolean
}
export default function AvailableDestinationCard({
  imageUrl,
  destinationName,
  name,
  price,
  rating,
  description,
  numberDays,
  numberNights,
  votes,
  isShowNumberOfVotes,
  children,
}: AvailableDestinationCardProps) {
  const theme = useTheme()
  const colorBlue = theme.palette.info.main
  const colorGrey = theme.mixins.grey.color

  return (
    <Box
      sx={{
        borderRadius: '15px',
        boxShadow: '0px 0px 0.875rem rgba(72, 64, 149, 0.14)',
        padding: '10px',
      }}
    >
      <img
        src={imageUrl || ImageIcon}
        alt={`${imageUrl || ImageIcon}`}
        style={{ borderRadius: '15px', height: '290px', width: '100%' }}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '27px',
          marginTop: '2px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'start',
            justifyContent: 'space-between',
            gap: '10px',
            width: '100%',
            height: '47px',
          }}
        >
          <Box sx={{ width: '60%' }}>
            <TextBold color={colorGrey} textAlign="justify">
              {destinationName.toUpperCase()}
            </TextBold>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'start',
            }}
          >
            <TextBold color={theme.mixins.soap.color}>{sinceText}</TextBold>
            <TextBold fontSize="18px" fontWeight={400} color={theme.mixins.soap.color}>
              USD {convertStringPrice(price)}
            </TextBold>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'start',
            justifyContent: 'space-between',
            gap: '10px',
            height: '47px',
          }}
        >
          <Box sx={{ width: '60%' }}>
            <TextParagraph color={colorGrey} fontSize="14px" fontWeight={300} textAlign="justify">
              {name?.toUpperCase()}
            </TextParagraph>
          </Box>
          <StyledRating value={rating} disabled />
        </Box>
        <Box sx={{ height: '90px', marginBottom: '33px' }}>
          <TextParagraph fontSize="14px" color={colorGrey}>
            {description}
          </TextParagraph>
        </Box>
      </Box>
      <Box sx={{ marginTop: '10px' }}>
        <TextParagraph fontSize="14px" color={colorGrey}>
          {forMoreInformationText}{' '}
          <span style={{ fontWeight: 700, color: theme.palette.secondary.main }}>
            {`"${showMoreText}"`}
          </span>
        </TextParagraph>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', margin: '33px 0 41px 0' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <TextSubTitle fontSize="14px" color={colorGrey}>
            {stayText}
          </TextSubTitle>
          <TextParagraph fontSize="14px" color={colorGrey} fontWeight={300}>
            {numberDays} {daysText} {numberNights} {nightsText}
          </TextParagraph>
        </Box>
        {isShowNumberOfVotes && (
          <Box
            sx={{
              border: `1px solid${colorBlue}`,
              borderRadius: '5px',
              padding: '4px 20px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <TextSubTitle fontSize="39px" color={colorBlue} lineHeight="40px">
                {votes}
              </TextSubTitle>
              <TextParagraph color={colorBlue}>{votesText}</TextParagraph>
            </Box>
          </Box>
        )}
      </Box>
      {children}
    </Box>
  )
}
